import { applyKeyToTransferAmount } from 'features/feeBasedInstantTransfer/helpers/transferAmount';
import {
  getTransferAmountErrorText,
  type TransferAmountError,
} from 'features/feeBasedInstantTransfer/helpers/transferAmountRules';
import type { TransferRoutePolicy } from 'features/feeBasedInstantTransfer/hooks/useSelectedRoutePolicy';
import { type Ref, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import AmountInput, {
  type AmountInputRef,
} from 'shared/components/vxl/AmountInput/AmountInput';
import Icon, { type IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isWeb } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

const CURRENCY_PREFIX = '$';
const ERROR_ICON: IconKeyType = 'STATUS_ERROR';

interface TransferAmountFieldProps {
  ref?: Ref<AmountInputRef>;
  amount: string;
  policy: TransferRoutePolicy;
  error?: TransferAmountError;
  onAmountChange: (amount: string) => void;
  isDisabled?: boolean;
  onFocus?: () => void;
}

function TransferAmountField({
  ref,
  amount,
  policy,
  error,
  onAmountChange,
  isDisabled = false,
  onFocus,
}: TransferAmountFieldProps) {
  const { t: translatedText } = useTranslation('feeBasedInstantTransfer', {
    keyPrefix: 'entry',
  });

  const getSpeedText = () => {
    if (error || policy.status !== 'ready') {
      return undefined;
    }
    const { speedDescription, feePercentageText } = policy;
    return speedDescription && feePercentageText
      ? translatedText('speedWithFee', {
          speed: speedDescription,
          fee: feePercentageText,
        })
      : speedDescription || feePercentageText;
  };

  const errorText = getTransferAmountErrorText(error, policy, translatedText);
  const captionText = errorText ?? getSpeedText();
  const speedIcon =
    !errorText && policy.status === 'ready' && policy.speedDescription
      ? policy.speedIcon
      : undefined;
  const hasNegativeCaption = Boolean(errorText) && error !== 'belowMinimum';
  const captionIcon = hasNegativeCaption ? ERROR_ICON : speedIcon;
  const captionColor = hasNegativeCaption
    ? theme.color.status.negative
    : theme.color.text.primary;

  useEffect(() => {
    if (!isWeb && errorText) {
      AccessibilityInfo.announceForAccessibility(errorText);
    }
  }, [errorText]);

  // AmpMaskView is a legacy view manager; structural changes inside it are not
  // reliably mounted under Fabric, so each mask wraps a subtree whose shape
  // never changes and the caption is rendered outside AmountInput.
  return (
    <View style={styles.container}>
      <AmplitudeMask>
        <AmountInput
          ref={ref}
          autoFocus={false}
          isDisabled={isDisabled}
          onFocus={onFocus}
          value={amount}
          onChangeText={onAmountChange}
          applyKey={applyKeyToTransferAmount}
          prefix={CURRENCY_PREFIX}
          accessibilityLabel={translatedText('amount')}
          showDoneButton={false}
        />
      </AmplitudeMask>
      {captionText ? (
        <View style={styles.caption}>
          {captionIcon ? (
            <Icon
              iconKey={captionIcon}
              iconSize="SM"
              iconColor={captionColor}
            />
          ) : null}
          <AmplitudeMask style={styles.captionMask}>
            <BodyXSText
              style={{ color: captionColor }}
              accessibilityRole="text"
              numberOfLines={0}
            >
              {captionText}
            </BodyXSText>
          </AmplitudeMask>
        </View>
      ) : null}
      {isWeb ? (
        <AmplitudeMask>
          <BodyXSText
            role="status"
            style={styles.webAnnouncement}
            numberOfLines={0}
          >
            {errorText ?? ''}
          </BodyXSText>
        </AmplitudeMask>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  caption: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xs,
  },
  captionMask: {
    flexShrink: 1,
  },
  container: {
    alignItems: 'center',
  },
  webAnnouncement: {
    height: 1,
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    width: 1,
  },
});

export default TransferAmountField;
